/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'
import scrollIntoView from 'scroll-into-view-if-needed'

import { useActiveHeadingId } from '@slite/components'
import { IndentedListIcon } from '@slite/icons'

import type { HelpArticleHeading } from '../../data/helpCenterFormat'

export function HelpToc({
  headings,
  scanLineOffset,
  scrollContainer,
}: {
  headings: HelpArticleHeading[]
  scanLineOffset: number
  /** The element that actually scrolls (the content box); defaults to window. */
  scrollContainer: HTMLElement | null
}) {
  const intl = useIntl()
  const activeId = useActiveHeadingId(headings, scanLineOffset, scrollContainer)

  if (headings.length === 0) {
    return null
  }
  const minLevel = Math.min(...headings.map((heading) => heading.level))
  const label = intl.formatMessage({
    id: 'website.helpCenter.toc.title',
    defaultMessage: 'On this page',
  })

  return (
    <nav aria-label={label} data-test-id="helpToc">
      {/* Pinned to the top of the scrolling column, like the sidebar search. */}
      <div
        className="flexbox flexbox-align-center flexbox-gap-12 font-size-7 font-weight-semibold shade-20"
        css={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--moon-dust-lighter)',
          padding: '32px 0 16px',
        }}
      >
        <IndentedListIcon size={14} />
        <span>{label}</span>
      </div>
      <ul
        css={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          borderLeft: '1px solid var(--shade-45)',
        }}
      >
        {headings.map((heading) => {
          const isActive = heading.id === activeId
          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                className={classNames('block', {
                  'shade-05 font-weight-semibold': isActive,
                  'shade-20 shade-05-on-hover': !isActive,
                })}
                css={{
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-7)',
                  lineHeight: 1.5,
                  padding: '4px 0',
                  paddingLeft: 12 + (heading.level - minLevel) * 12,
                  marginLeft: -1,
                  borderLeft: `1px solid ${isActive ? 'var(--shade-05)' : 'transparent'}`,
                  transition:
                    'color .2s cubic-bezier(.215,.61,.355,1), border-color .2s cubic-bezier(.215,.61,.355,1)',
                }}
                onClick={(event) => {
                  const element = document.getElementById(heading.id)
                  if (!element) {
                    return
                  }
                  event.preventDefault()
                  scrollIntoView(element, {
                    behavior: 'smooth',
                    block: 'start',
                  })
                  window.history.replaceState(null, '', `#${heading.id}`)
                }}
              >
                {heading.title}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
